/**
 * Shared exam-status language, used identically on every surface that lists
 * exams (landing catalog, nav dropdown, /exams browse grid, leaderboard).
 *
 *   live        → green dot   "complete — identical to the real exam"
 *   beta        → amber pill  "full runs, one polish layer pending"
 *   coming_soon → dashed gray "notify me"
 */
import { EXAM_CONFIGS, type ExamType, type ExamStatus } from "@/lib/exam/config";

export function statusOf(slug: string): ExamStatus {
  return EXAM_CONFIGS[slug as ExamType]?.status ?? "coming_soon";
}

/** Hand-written one-liners for beta exams where we know exactly what's
 *  pending (kept in one place so all four catalog surfaces say the same
 *  thing). Exams not listed fall back to a mediaStatus-derived line. */
// 2026-07-19 sweep: "audio coming" notes were stale — every listening exam has
// had 100% real audio since the coverage audit; remaining notes reflect the
// actual caveat per exam (difficulty calibration tier, format approximations).
const BETA_NOTES: Partial<Record<ExamType, string>> = {
  gmat: "full runs · section-adaptive difficulty approximated",
  hsk: "full runs with audio · difficulty tuning in progress",
  jlpt: "full runs with audio · difficulty tuning in progress",
  topik: "full runs with audio · difficulty tuning in progress",
  delf: "full runs with audio · difficulty tuning in progress",
  "goethe-c1": "full runs with audio · 3 forms — more coming",
  pte: "full runs with audio · difficulty tuning in progress",
  oet: "role-play speaking simulated — full runs otherwise",
  fsp: "spoken parts simulated in text — full runs otherwise",
  "duolingo-english": "adaptive format approximated — full runs",
  lsat: "full runs · analytical reasoning retired-format",
  mcat: "full runs · passage bank still growing",
  ap: "free-response shown but not auto-scored — score reflects MCQ",
};

/** One-line honest caveat for beta exams. */
export function betaCaveat(slug: string): string | null {
  const cfg = EXAM_CONFIGS[slug as ExamType];
  if (!cfg || cfg.status !== "beta") return null;
  const note = BETA_NOTES[slug as ExamType];
  if (note) return note;
  if (cfg.mediaStatus === "text-only") {
    const hasListening = cfg.sections.some(s => /listening|audio/i.test(s.label));
    return hasListening ? "listening shows transcripts — audio coming" : "full runs · report polish pending";
  }
  if (cfg.mediaStatus === "placeholder") return "question bank being upgraded";
  return "full runs · report polish pending";
}

export function StatusDot({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full bg-emerald-500 ${className}`}
      title="Complete — identical to the real exam"
    />
  );
}

export function BetaPill({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-block text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 ${className}`}
      title="Works end to end — one polish layer pending"
    >
      beta
    </span>
  );
}

/** Compact legend used next to section headers. */
export function StatusLegend({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 text-[11px] text-[var(--text-muted)] ${className}`}>
      <span className="flex items-center gap-1.5"><StatusDot /> complete</span>
      <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full bg-amber-400" /> beta</span>
      <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full bg-gray-300" /> coming soon</span>
    </div>
  );
}
